import { Link } from "react-router-dom";
import { Button, Tooltip, Checkbox } from "@altimateai/lego";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Badge } from "@altimateai/lego";
import React from "react";
import { Privacy } from "@/helpers/constants";
import { KnowledgeCatalogItem } from "./types";
import { formatDate } from "@/helpers/utils";
import ForkKnowledgeBaseTemplate from "./ForkKnowledgeBaseTemplate";
import { statusColors } from "./constants";
import PrivacyAndCreatorInfo from "@/components/PrivacyAndCreatorInfo";

interface KnowledgeCatalogCardProps {
  item: KnowledgeCatalogItem;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onClick?: (itemId: number) => void;
  className?: string;
  openInNewTab?: boolean;
}

export default function KnowledgeCatalogCard({
  item,
  showCheckbox = false,
  isSelected = false,
  onClick,
  className = "",
  openInNewTab = false,
}: KnowledgeCatalogCardProps) {
  const isSource = item.type !== "template";

  const link = isSource ? `/knowledge-hub/${item.id}` : `/knowledge-hub/template/${item.name}`;

  const handleCardClick = () => {
    if (onClick) {
      onClick(item.id);
    }
  };

  return (
    <Card
      className={`al-w-full md:al-w-[300px] ${
        showCheckbox && isSelected ? "al-ring-2 al-ring-blue-500 al-bg-blue-50" : ""
      } ${onClick ? "al-cursor-pointer al-transition-colors" : ""} ${className}`}
      onClick={onClick ? handleCardClick : undefined}
      aria-label={`${item.name} card`}
    >
      <CardHeader>
        <Stack direction="row" align="center">
          {showCheckbox && (
            <Checkbox
              checked={isSelected}
              className="al-mr-3 al-flex-shrink-0"
              aria-label={`Select ${item.name}`}
            />
          )}
          <div className="al-flex-1 al-min-w-0">
            <Tooltip title={item.name}>
              <CardTitle className="al-truncate">
                {item.icon ? <b className="al-mr-2">{item.icon}</b> : null}
                {onClick ? item.name : <Link to={link}>{item.name}</Link>}
              </CardTitle>
            </Tooltip>
          </div>
        </Stack>
        {isSource && item.user_id && (
          <>
            <PrivacyAndCreatorInfo
              privacy={item.privacy || Privacy.PRIVATE}
              userId={item.user_id}
            />
          </>
        )}
      </CardHeader>
      <CardContent className="al-flex-grow">
        <Stack direction="column" className="al-mb-4">
          <p className="al-text-sm al-text-gray-600 al-line-clamp-2 al-min-h-[40px]">
            {item.description}
          </p>
          {isSource && item.updated_at && (
            <p className="al-text-xs al-text-gray-500">
              Last Updated: {formatDate(item.updated_at)}
            </p>
          )}
        </Stack>
      </CardContent>
      <CardFooter>
        <Stack direction="row" justify="between" align="center" className="al-w-full">
          {!isSource && <ForkKnowledgeBaseTemplate templateName={item.name} />}
          {item.forked ? (
            <Badge variant="outline" className={statusColors["Forked"]}>
              Forked
            </Badge>
          ) : null}
          <Link
            to={link}
            className="al-ml-auto"
            target={openInNewTab ? "_blank" : undefined}
            rel={openInNewTab ? "noopener noreferrer" : undefined}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Button className="al-w-full" size="sm">
              View Details
            </Button>
          </Link>
        </Stack>
      </CardFooter>
    </Card>
  );
}
